import { useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { BreakDecisionOption } from '@runhuman/shared';
import { BreakDecisionAlternativeRow } from './BreakDecisionAlternativeRow';
import {
  buildBreakDecisionModalStyles,
  type BreakDecisionModalStyles,
} from './break-decision-modal-styles';
import type { BreakRequestsTheme } from './types';

export interface BreakDecisionModalProps {
  readonly testerAlias: string;
  /** Wall-clock label of the originally-requested window. Rendered in the header. */
  readonly currentWindowLabel: string;
  /**
   * Caller-supplied composer for the "Custom window" section. Each surface
   * injects its own — the web admin Counter modal supplies a schedule-aware
   * start + duration picker constrained to the tester's shift blocks; the
   * Discord-Activity drawer (no schedule on hand) supplies the schedule-free
   * {@link SimpleBreakWindowComposer}. Both wire their Suggest / Schedule
   * actions to the same `onOffer` / `onSchedule` callbacks passed below.
   */
  readonly customWindow: ReactNode;
  /**
   * Engine-suggested options for this request — the modal filters to
   * `kind === 'alternative'` and renders one row per alternative inside the
   * "Alternatives" section. Empty list collapses the section without
   * affecting the "Custom window" composer.
   */
  readonly decisionOptions: readonly BreakDecisionOption[];
  readonly timezone: string;
  readonly busy: boolean;
  readonly theme: BreakRequestsTheme;
  /** `[Offer]` from either section — admin counter-offers the chosen window. */
  readonly onOffer: (params: OfferParams) => void;
  /** `[Schedule]` from either section — admin approves the alternative directly. */
  readonly onSchedule: (params: ScheduleParams) => void;
  readonly onCancel: () => void;
}

export interface OfferParams {
  readonly startsAt: string;
  readonly endsAt: string;
  readonly rationale: string | undefined;
}

export interface ScheduleParams {
  readonly startsAt: string;
  readonly endsAt: string;
}

/**
 * Combined admin decision modal — replaces the legacy `<CounterOfferModal>`
 * on admin pending rows by folding two surfaces into one:
 *
 *   1. **Alternatives** — engine-suggested `BreakDecisionOption`s in a
 *      compact table-row layout. Each row exposes trailing `[Offer]` and
 *      `[Schedule]` actions: Offer routes through `counterOfferAsAdmin`
 *      (tester accepts/declines next round), Schedule routes through
 *      `decideBreakRequest({ decision: 'approve-alternative', … })` (the
 *      break is approved immediately, no counter round).
 *   2. **Custom window** — a caller-injected `customWindow` composer that
 *      builds a window as start + duration (never a cross-day end) with the
 *      same `[Suggest]` / `[Schedule]` split.
 *
 * Mounted by the web admin's `<BreakRequestRow>` and the Discord-Activity
 * `<BreakRequestDrawer>`; both surfaces inject their theme via the
 * {@link BreakRequestsTheme} prop and wire the callbacks to their own API
 * client. The shared widget never owns auth or routing — that stays at the
 * caller.
 */
export function BreakDecisionModal({
  testerAlias,
  currentWindowLabel,
  customWindow,
  decisionOptions,
  timezone,
  busy,
  theme,
  onOffer,
  onSchedule,
  onCancel,
}: BreakDecisionModalProps) {
  const styles = useMemo(() => buildBreakDecisionModalStyles(theme), [theme]);
  const alternatives = useMemo(
    () => decisionOptions.filter((o) => o.kind === 'alternative'),
    [decisionOptions],
  );

  const handleAlternativeOffer = (option: BreakDecisionOption) => {
    onOffer({
      startsAt: option.startsAt,
      endsAt: option.endsAt,
      rationale: option.rationale,
    });
  };
  const handleAlternativeSchedule = (option: BreakDecisionOption) => {
    onSchedule({ startsAt: option.startsAt, endsAt: option.endsAt });
  };

  // Portal to <body> so the backdrop's stacking context is the document root,
  // not whatever ancestor (sticky coverage rows, isolated lane cells) the
  // mount point happens to sit inside. Without this the modal can paint
  // behind sticky-positioned grid chrome on the schedule page.
  return createPortal(
    <div style={styles.backdrop} role="dialog" aria-modal="true" onClick={onCancel}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <h4 style={styles.title}>Counter-offer</h4>
        <div style={styles.headerMeta}>
          <div>{testerAlias}</div>
          <div style={styles.mutedLine}>Currently: {currentWindowLabel}</div>
        </div>

        {alternatives.length > 0 && (
          <AlternativesSection
            alternatives={alternatives}
            timezone={timezone}
            busy={busy}
            styles={styles}
            onOffer={handleAlternativeOffer}
            onSchedule={handleAlternativeSchedule}
          />
        )}

        <div style={styles.sectionHeading}>Custom window</div>
        {customWindow}

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.ghostButton}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface AlternativesSectionProps {
  readonly alternatives: readonly BreakDecisionOption[];
  readonly timezone: string;
  readonly busy: boolean;
  readonly styles: BreakDecisionModalStyles;
  readonly onOffer: (option: BreakDecisionOption) => void;
  readonly onSchedule: (option: BreakDecisionOption) => void;
}

function AlternativesSection({
  alternatives,
  timezone,
  busy,
  styles,
  onOffer,
  onSchedule,
}: AlternativesSectionProps) {
  return (
    <>
      <div style={styles.sectionHeading}>Alternatives</div>
      <div style={styles.sectionList}>
        {alternatives.map((option) => (
          <BreakDecisionAlternativeRow
            key={`${option.startsAt}:${option.endsAt}`}
            option={option}
            timezone={timezone}
            busy={busy}
            styles={styles}
            onOffer={onOffer}
            onSchedule={onSchedule}
          />
        ))}
      </div>
    </>
  );
}
